import { FC, useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useSnapshot } from 'valtio'
import { useSearchParams } from 'react-router-dom'
import { Button, Tooltip, Typography } from '@mui/material'
import {
  CurrentTestState,
  CurrentUserAssessmentState,
  completeTest,
  ScoringState,
  getCurrentResponseItemIndexById,
} from '@app/storage'
import { announce, NavAnnounceState } from '@app/components/common/ScreenReaderAnnouncer'
import { QuestionContextMenuContext } from '@app/components/common/QuestionContextMenuContext'
import { Feedback, NavigationMode, TestFeedbackAccess } from '@app/types'
import { AssessmentItemResponse } from '@app/models'
import { BasicModal } from '@app/components/ui/modals/BasicModal'
import { ItemsListModal } from '@app/components/ui/modals/ItemsListModal'
import { SubmitTestModal } from '@app/components/ui/modals/SubmitTestModal'
import { TestFeedbackComponent } from '@app/components/feedback/TestFeedback'
import {
  ButtonsWrapper,
  FooterWrapper,
  BubbleNavigationWrapper,
} from '@app/components/ui/containers/ItemListCarousel'
import ItemList from '@app/components/ui/containers/ItemListContatainer'
import { TestReviewNavigator } from './TestReviewNavigator'
import { TestTakingNavigator } from './TestTakingNavigator'
import {
  FINISH_TEST_BTN_ATTRIBUTES,
  RETURN_TO_DASHBOARD_BTN_ATTRIBUTES,
  FINAL_FEEDBACK_MODAL_ATTRIBUTES,
  BACK_TO_OVERVIEW_BTN_ATTRIBUTES,
  SUCCESS_THRESHOLD,
} from '@app/constants'
import { getTerm } from '@app/helpers/workkeysReplaceTerms'
import { isSingleQuestionWritingTest } from '@app/helpers/isSingleQuestionWritingTest'
import {
  FeedbackEvent,
  useFindUnAnsweredItems,
  useItemResponsesScoring,
  useTestCallback,
  getReturnButtonText,
  getReturnMessage,
} from '@app/helpers'
import StudentFeedbackModalContent from '../StudentFeedbackModalContent'

const testTerm = getTerm('Test', true)

interface ItemsCarouselProps {
  onChange: (id: string) => void
}

export const ItemsNavigator: FC<ItemsCarouselProps> = ({ onChange }) => {
  const { groupMode, isQuestionPreviewMode } = useSnapshot(ScoringState)
  const { currentTestPart, itemsResponses, currentTest } = useSnapshot(CurrentTestState)
  const { assessmentSettings, program, subject } = useSnapshot(CurrentUserAssessmentState)
  const markedItems = useContext(QuestionContextMenuContext)?.markedItems ?? []
  const [searchParams] = useSearchParams()

  const [openModal, setOpenModal] = useState(false)
  const [openSubmitModal, setOpenSubmitModal] = useState(false)
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false)
  const [pendingFeedback, setPendingFeedback] = useState(false)
  const [testFeedback, setTestFeedback] = useState<Feedback[]>([])
  const unAnsweredItems = useFindUnAnsweredItems()
  const navItemsScoring = useItemResponsesScoring(
    itemsResponses as AssessmentItemResponse[],
    SUCCESS_THRESHOLD,
  )
  const reviewItemsScoring =
    subject?.toLowerCase() === 'writing' && !groupMode ? undefined : navItemsScoring

  // A single-question Writing test has one bubble to navigate to, so the whole
  // navigator is noise — in review as well as while taking it. Review's variant
  // carries score bubbles, but for Writing outside group mode reviewItemsScoring
  // is undefined anyway, and the score is shown by WritingScorePanel instead.
  const hideQuestionNavigation = useMemo(
    () => isSingleQuestionWritingTest({ program, subject, itemCount: itemsResponses.length }),
    [program, subject, itemsResponses.length],
  )
  const modalButtonRef = useRef(null)
  const navigatedInModalRef = useRef(false)
  const [submitFeedback, setSubmitFeedback] = useState<(() => Promise<boolean>) | null>(null)
  const [isCombinedFeedback, setIsCombinedFeedback] = useState<boolean>(false)
  const [hasRatingToSubmit, setHasRatingToSubmit] = useState<boolean>(false)
  const [, setIsSubmitting] = useState<boolean>(false)

  const isLinearMode = useMemo(
    () => currentTestPart && currentTestPart.navigationMode === NavigationMode.linear,
    [currentTestPart],
  )

  useEffect(() => {
    if (currentTest && currentTest.testFeedback) {
      setTestFeedback(currentTest.testFeedback as Feedback[])
    }
  }, [currentTest, currentTest.testFeedback])

  const openQuestionsModal = () => {
    setOpenModal(current => !current)
  }

  // a11y: popup aria-hides the footer — flag open so the footer mutes its
  // (soon-stale) live region; reset the nav flag each open. Cleared on onExited.
  useEffect(() => {
    if (openModal) {
      NavAnnounceState.modalOpen = true
      navigatedInModalRef.current = false
    }
  }, [openModal])

  // a11y: if we unmount while the popup is open (auto-submit, route change)
  // onExited never fires — reset the global so it doesn't mute the footer forever.
  useEffect(() => {
    return () => {
      NavAnnounceState.modalOpen = false
    }
  }, [])

  const closeQuestionsModal = () => {
    setOpenModal(false)
  }

  // a11y: popup fully closed and page un-hidden — announce the move now so
  // ChromeVox reads the current question, not a stale pre-hide snapshot.
  const handleQuestionsModalExited = () => {
    NavAnnounceState.modalOpen = false
    if (!navigatedInModalRef.current) return
    navigatedInModalRef.current = false
    const current = CurrentTestState.currentItemResponse
    if (!current?.id) return
    const index = getCurrentResponseItemIndexById(current.id)
    if (index < 0) return
    const markedSuffix =
      !currentTest.isFinished && markedItems.includes(current.itemId) ? ', marked for review' : ''
    announce(`You moved to question ${index + 1}${markedSuffix}.`)
  }

  const closeUnansweredModal = () => {
    setOpenSubmitModal(false)
  }

  const submitTestPart = () => {
    completeTest()

    if (openSubmitModal) {
      // a11y: Defer opening the feedback modal until SubmitTestModal's exit
      // transition finishes. Otherwise both dialogs' <h2> titles overlap in
      // the accessibility tree and NVDA announces both ("Are you ready..."
      // and "Congrats!") while transitioning.
      setPendingFeedback(true)
      closeUnansweredModal()
      return
    }

    setOpenFeedbackModal(true)
  }

  const handleSubmitModalExited = () => {
    if (pendingFeedback) {
      setPendingFeedback(false)
      setOpenFeedbackModal(true)
    }
  }

  const selectItem = (id: string) => () => {
    if (openSubmitModal) {
      closeUnansweredModal()
    }

    // a11y: remember that a selection inside the questions popup changed the
    // question, so handleQuestionsModalExited can announce it after close.
    if (NavAnnounceState.modalOpen && id !== CurrentTestState.currentItemResponse?.id) {
      navigatedInModalRef.current = true
    }

    onChange(id)
  }

  const handleFinishTestPart = () => {
    setOpenSubmitModal(true)
  }

  const onTestCallback = useTestCallback()

  const origin = searchParams.get('origin')
  const previewParam = searchParams.get('preview') || ''
  const preview = /^true$/i.test(previewParam)
  const isDemoMode = origin === 'demo'

  const leaveButtonText = useMemo(() => {
    return `Leave ${preview ? 'Preview' : 'Review'}`
  }, [preview])

  const submitButtonTooltip = useMemo(() => {
    return unAnsweredItems?.length > 0 ? 'You have unanswered questions' : ''
  }, [unAnsweredItems?.length])

  const handleReturnToStudyPlan = useCallback(async () => {
    if (isCombinedFeedback && submitFeedback && hasRatingToSubmit) {
      setIsSubmitting(true)
      try {
        await submitFeedback()
        // Wait for snackbar to appear and be visible to the user
        setTimeout(() => {
          setIsSubmitting(false)
          setOpenFeedbackModal(false)
          onTestCallback()
        }, 1500)
      } catch (error) {
        console.error('Error submitting feedback:', error)
        setIsSubmitting(false)
      }
    } else {
      setOpenFeedbackModal(false)
      onTestCallback()
    }
  }, [
    isCombinedFeedback,
    submitFeedback,
    hasRatingToSubmit,
    setIsSubmitting,
    setOpenFeedbackModal,
    onTestCallback,
  ])

  const handleSubmitCallback = useCallback(
    (submitFunc: () => Promise<boolean>, isCombined: boolean, hasRating: boolean) => {
      setSubmitFeedback(() => submitFunc)
      setIsCombinedFeedback(isCombined)
      setHasRatingToSubmit(hasRating)
    },
    [],
  )

  const handleReviewTest = useCallback(() => {
    setOpenFeedbackModal(false)
    window.location.reload()
  }, [])

  return (
    <FooterWrapper isQuestionPreviewMode={isQuestionPreviewMode} hideNavigation={hideQuestionNavigation}>
      {!isQuestionPreviewMode && !hideQuestionNavigation && (
        <BubbleNavigationWrapper component='nav'>
          {currentTest.isFinished ? (
            <TestReviewNavigator
              modalButtonRef={modalButtonRef}
              openQuestionsModal={openModal}
              onShowQuestionsModal={openQuestionsModal}
              openSubmitModal={openSubmitModal}
              isLinearMode={isLinearMode}
              itemsScoring={reviewItemsScoring}
              onSelect={selectItem}
            />
          ) : (
            <TestTakingNavigator
              modalButtonRef={modalButtonRef}
              onShowQuestionsModal={openQuestionsModal}
              isQuestionsModalOpen={openModal}
            />
          )}
        </BubbleNavigationWrapper>
      )}

      <ButtonsWrapper>
        {currentTest.isFinished ? (
          <Button
            variant='contained'
            color='grey'
            size='small'
            aria-label={leaveButtonText}
            role='button'
            onClick={onTestCallback}
            {...BACK_TO_OVERVIEW_BTN_ATTRIBUTES}
          >
            {leaveButtonText}
          </Button>
        ) : (
          <Tooltip title={submitButtonTooltip} arrow={true}>
            <Button
              variant='outlined'
              color='secondary'
              size='small'
              aria-label={
                submitButtonTooltip ? `Submit ${testTerm}. ${submitButtonTooltip}` : `Submit ${testTerm}`
              }
              role='button'
              onClick={handleFinishTestPart}
              {...FINISH_TEST_BTN_ATTRIBUTES}
            >
              {`Submit ${testTerm}`}
            </Button>
          </Tooltip>
        )}
      </ButtonsWrapper>

      <SubmitTestModal
        open={openSubmitModal}
        onClose={closeUnansweredModal}
        onSubmit={submitTestPart}
        onExited={handleSubmitModalExited}
        isLinearMode={isLinearMode}
        hasUnansweredItems={!!unAnsweredItems.length}
        onSelectItem={selectItem}
        duration={assessmentSettings.timeLimits ?? 0}
      />

      <BasicModal
        open={openFeedbackModal}
        size='sm'
        backdropDisabled={!isDemoMode}
        onClose={isDemoMode ? handleReviewTest : () => setOpenFeedbackModal(false)}
        onSubmit={handleReturnToStudyPlan}
        title={!testFeedback.length && `Congrats! You submitted ${currentTest.title}`}
        noCancel={!isDemoMode}
        cancelText={isDemoMode ? `Review ${testTerm}` : null}
        submitText={getReturnButtonText(origin)}
        modalProps={FINAL_FEEDBACK_MODAL_ATTRIBUTES}
        submitButtonProps={RETURN_TO_DASHBOARD_BTN_ATTRIBUTES}
        footer={
          !isDemoMode && (
            <StudentFeedbackModalContent
              event={FeedbackEvent.COMPLETE_PRACTICE_TEST}
              testName={currentTest.title}
              onSubmitCallback={handleSubmitCallback}
            />
          )
        }
      >
        {testFeedback.length ? (
          testFeedback
            .filter(feedback => feedback.access === TestFeedbackAccess.atEnd)
            .map(feedback => <TestFeedbackComponent feedback={feedback} key={feedback.id} />)
        ) : isDemoMode ? (
          <Typography variant='body1'>Test submitted successfully.</Typography>
        ) : (
          <Typography variant='body1'>{getReturnMessage(origin)}</Typography>
        )}
      </BasicModal>

      <ItemsListModal
        open={openModal}
        onClose={closeQuestionsModal}
        onExited={handleQuestionsModalExited}
        anchorEl={modalButtonRef.current}
        isReviewMode={currentTest.isFinished}
      >
        <ItemList
          onClick={selectItem}
          openSubmitModal={openSubmitModal}
          isLinearMode={isLinearMode}
          showAnsweredStatus={true}
          isOpenModal={openModal}
          closeModal={closeQuestionsModal}
          itemsScoring={reviewItemsScoring}
        ></ItemList>
      </ItemsListModal>
    </FooterWrapper>
  )
}
